import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search,
    Eye,
    Calendar,
    Filter,
    RefreshCw,
    CheckCircle,
    XCircle,
    Clock,
    AlertCircle,
    Building2,
    User,
    DollarSign,
    TrendingUp,
    TrendingDown,
    X,
    Edit,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import {
    useGetAllBookingsQuery,
    useUpdateBookingStatusMutation,
    useProcessRefundMutation,
    Booking,
    BookingListParams
} from '../../store/slices/apiSlice';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';

const BookingManagement = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'confirmed' | 'cancelled' | 'completed'>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Modal states
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

    // Status update form state
    const [statusForm, setStatusForm] = useState({
        status: 'pending' as 'pending' | 'confirmed' | 'cancelled' | 'completed',
        cancellation_reason: ''
    });

    // Refund form state
    const [refundForm, setRefundForm] = useState({
        refund_amount: '',
        notes: ''
    });

    // Build query params
    const queryParams: BookingListParams = {
        page: currentPage,
        limit: itemsPerPage,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
    };

    // Fetch bookings
    const { data, isLoading, error, refetch } = useGetAllBookingsQuery(queryParams);
    const [updateBookingStatus, { isLoading: isUpdatingStatus }] = useUpdateBookingStatusMutation();
    const [processRefund, { isLoading: isProcessingRefund }] = useProcessRefundMutation();

    // Extract bookings from API response
    const bookings = data?.data?.bookings || [];
    const pagination = data?.data?.pagination;

    // Filter bookings based on search
    const filteredBookings = bookings.filter((booking: Booking) => {
        const matchesSearch =
            booking.booking_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            booking.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            booking.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            booking.apartment?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            booking.apartment?.location?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    // Statistics
    const totalBookings = pagination?.total_bookings || 0;
    const pendingBookings = bookings.filter((b: Booking) => b.status === 'pending').length;
    const confirmedBookings = bookings.filter((b: Booking) => b.status === 'confirmed').length;
    const cancelledBookings = bookings.filter((b: Booking) => b.status === 'cancelled').length;
    const completedBookings = bookings.filter((b: Booking) => b.status === 'completed').length;

    // Handle status modal open
    const handleStatusModalOpen = (booking: Booking) => {
        setSelectedBooking(booking);
        setStatusForm({
            status: booking.status,
            cancellation_reason: booking.cancellation_reason || ''
        });
        setShowStatusModal(true);
    };

    // Handle refund modal open
    const handleRefundModalOpen = (booking: Booking) => {
        if (booking.status !== 'cancelled') {
            toast.error('Refund can only be processed for cancelled bookings');
            return;
        }
        if (booking.is_refunded) {
            toast.error('Refund has already been processed for this booking');
            return;
        }
        setSelectedBooking(booking);
        setRefundForm({
            refund_amount: booking.refund_amount?.toString() || '',
            notes: ''
        });
        setShowRefundModal(true);
    };

    // Handle view details
    const handleViewDetails = (booking: Booking) => {
        navigate(`/admin/bookings/${booking.booking_id}`);
    };

    // Handle items per page change
    const handleItemsPerPageChange = (newItemsPerPage: number) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1); // Reset to first page when changing items per page
    };

    // Handle status update
    const handleUpdateStatus = async () => {
        if (!selectedBooking) return;

        try {
            await updateBookingStatus({
                booking_id: selectedBooking.booking_id,
                status: statusForm.status,
                cancellation_reason: statusForm.status === 'cancelled' ? statusForm.cancellation_reason || 'Cancelled by admin' : null
            }).unwrap();

            toast.success('Booking status updated successfully!');
            setShowStatusModal(false);
            setSelectedBooking(null);
            refetch();
        } catch (error: any) {
            console.error('Failed to update booking status:', error);
            toast.error(error?.data?.message || 'Failed to update booking status');
        }
    };

    // Handle refund
    const handleProcessRefund = async () => {
        if (!selectedBooking || !refundForm.refund_amount) return;

        try {
            const refundAmount = parseFloat(refundForm.refund_amount);
            if (isNaN(refundAmount) || refundAmount <= 0) {
                toast.error('Please enter a valid refund amount');
                return;
            }

            await processRefund({
                booking_id: selectedBooking.booking_id,
                refund_amount: refundAmount,
                notes: refundForm.notes || null
            }).unwrap();

            toast.success('Refund processed successfully!');
            setShowRefundModal(false);
            setSelectedBooking(null);
            setRefundForm({ refund_amount: '', notes: '' });
            refetch();
        } catch (error: any) {
            console.error('Failed to process refund:', error);
            toast.error(error?.data?.message || 'Failed to process refund');
        }
    };

    // Get status color
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'confirmed':
                return 'bg-green-100 text-green-800';
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'cancelled':
                return 'bg-red-100 text-red-800';
            case 'completed':
                return 'bg-blue-100 text-blue-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    // Get status icon
    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'confirmed':
                return <CheckCircle className="h-4 w-4 text-green-600" />;
            case 'pending':
                return <Clock className="h-4 w-4 text-yellow-600" />;
            case 'cancelled':
                return <XCircle className="h-4 w-4 text-red-600" />;
            case 'completed':
                return <CheckCircle className="h-4 w-4 text-blue-600" />;
            default:
                return <AlertCircle className="h-4 w-4 text-gray-600" />;
        }
    };

    // Format date
    const formatDate = (dateString: string | Date | null | undefined) => formatDateUtil(dateString);;

    // Format currency
    const formatCurrency = (amount: number) => {
        return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Generate page numbers for pagination
    const getPageNumbers = () => {
        if (!pagination) return [];
        const pages = [];
        const maxVisiblePages = 5;
        const totalPages = pagination.total_pages;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            const startPage = Math.max(1, currentPage - 2);
            const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
        }

        return pages;
    };

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <div className="flex items-center">
                        <XCircle className="h-6 w-6 text-red-600 mr-3" />
                        <div>
                            <h3 className="text-lg font-semibold text-red-800">Error Loading Bookings</h3>
                            <p className="text-red-600 mt-1">There was an error loading the bookings. Please try again.</p>
                            <button
                                onClick={() => refetch()}
                                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Booking Management</h1>
                        <p className="text-gray-600">Manage apartment bookings and refunds</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => refetch()}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Bookings</p>
                            <p className="text-3xl font-bold text-gray-900">{totalBookings}</p>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-full">
                            <Calendar className="h-6 w-6 text-blue-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Pending</p>
                            <p className="text-3xl font-bold text-yellow-600">{pendingBookings}</p>
                        </div>
                        <div className="p-3 bg-yellow-100 rounded-full">
                            <Clock className="h-6 w-6 text-yellow-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Confirmed</p>
                            <p className="text-3xl font-bold text-green-600">{confirmedBookings}</p>
                        </div>
                        <div className="p-3 bg-green-100 rounded-full">
                            <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Cancelled</p>
                            <p className="text-3xl font-bold text-red-600">{cancelledBookings}</p>
                        </div>
                        <div className="p-3 bg-red-100 rounded-full">
                            <XCircle className="h-6 w-6 text-red-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Completed</p>
                            <p className="text-3xl font-bold text-blue-600">{completedBookings}</p>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-full">
                            <CheckCircle className="h-6 w-6 text-blue-600" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Search bookings, users, apartments..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <select
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value as any);
                                    setCurrentPage(1);
                                }}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="all">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="completed">Completed</option>
                            </select>
                        </div>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                type="date"
                                placeholder="Start Date"
                                value={startDate}
                                onChange={(e) => {
                                    setStartDate(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                type="date"
                                placeholder="End Date"
                                value={endDate}
                                onChange={(e) => {
                                    setEndDate(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Bookings Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">Bookings ({filteredBookings.length})</h2>
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">Items per page:</label>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </select>
                        </div>
                    </div>
                </div>

                {filteredBookings.length === 0 ? (
                    <div className="p-12 text-center">
                        <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings found</h3>
                        <p className="text-gray-600 mb-4">No bookings match your current filters.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Apartment</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check-in</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check-out</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guests</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Refund</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredBookings.map((booking: Booking) => (
                                    <tr key={booking.booking_id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-medium text-gray-900">{booking.booking_reference}</div>
                                            <div className="text-xs text-gray-500">ID: {booking.booking_id}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-medium text-gray-900">{booking.user?.name}</div>
                                            <div className="text-xs text-gray-500">{booking.user?.email}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                {booking.apartment?.image && (
                                                    <img
                                                        src={booking.apartment.image}
                                                        alt={booking.apartment.name}
                                                        className="w-10 h-10 object-cover rounded-lg mr-3"
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).style.display = 'none';
                                                        }}
                                                    />
                                                )}
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{booking.apartment?.name}</div>
                                                    <div className="text-xs text-gray-500">{booking.apartment?.location}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{formatDate(booking.check_in_date)}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{formatDate(booking.check_out_date)}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center text-sm text-gray-900">
                                                <User className="h-4 w-4 mr-1 text-gray-400" />
                                                {booking.guests}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                {getStatusIcon(booking.status)}
                                                <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                                                    {booking.status}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {booking.is_refunded ? (
                                                <div className="text-sm">
                                                    <div className="text-green-600 font-medium">Refunded</div>
                                                    {booking.refund_amount && (
                                                        <div className="text-xs text-gray-500">{formatCurrency(booking.refund_amount)}</div>
                                                    )}
                                                </div>
                                            ) : booking.status === 'cancelled' ? (
                                                <button
                                                    onClick={() => handleRefundModalOpen(booking)}
                                                    className="text-sm text-blue-600 hover:text-blue-900 font-medium"
                                                >
                                                    Process Refund
                                                </button>
                                            ) : (
                                                <span className="text-sm text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => handleViewDetails(booking)}
                                                    className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-transparent hover:border-green-300 hover:shadow-sm"
                                                    title="View booking details"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleStatusModalOpen(booking)}
                                                    className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-transparent hover:border-green-300 hover:shadow-sm"
                                                    title="Update status"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {pagination && pagination.total_pages > 1 && (
                    <div className="bg-white rounded-lg shadow border border-gray-100 p-4 mt-6">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                                Showing page {currentPage} of {pagination.total_pages} ({pagination.total_bookings} total bookings)
                            </div>

                            <div className="flex items-center space-x-2">
                                {/* First Page */}
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="First page"
                                >
                                    <ChevronsLeft className="w-4 h-4" />
                                </button>

                                {/* Previous Page */}
                                <button
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Previous page"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>

                                {/* Page Numbers */}
                                <div className="flex items-center space-x-1">
                                    {getPageNumbers().map((pageNum) => (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                currentPage === pageNum
                                                    ? 'bg-blue-600 text-white'
                                                    : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    ))}
                                </div>

                                {/* Next Page */}
                                <button
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === pagination.total_pages}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Next page"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>

                                {/* Last Page */}
                                <button
                                    onClick={() => setCurrentPage(pagination.total_pages)}
                                    disabled={currentPage === pagination.total_pages}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Last page"
                                >
                                    <ChevronsRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Status Update Modal */}
            <Modal
                isOpen={showStatusModal}
                onClose={() => {
                    setShowStatusModal(false);
                    setSelectedBooking(null);
                }}
                title="Update Booking Status"
            >
                <div className="space-y-4">
                    {selectedBooking && (
                        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                            <h4 className="font-medium text-gray-900">Booking: {selectedBooking.booking_reference}</h4>
                            <p className="text-sm text-gray-600">Apartment: {selectedBooking.apartment?.name}</p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                        <select
                            value={statusForm.status}
                            onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value as any })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="completed">Completed</option>
                        </select>
                    </div>

                    {statusForm.status === 'cancelled' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Cancellation Reason</label>
                            <textarea
                                value={statusForm.cancellation_reason}
                                onChange={(e) => setStatusForm({ ...statusForm, cancellation_reason: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                rows={3}
                                placeholder="Enter cancellation reason (optional)"
                            />
                        </div>
                    )}

                    <div className="flex justify-end space-x-3 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowStatusModal(false);
                                setSelectedBooking(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleUpdateStatus}
                            disabled={isUpdatingStatus}
                        >
                            {isUpdatingStatus ? 'Updating...' : 'Update Status'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Refund Modal */}
            <Modal
                isOpen={showRefundModal}
                onClose={() => {
                    setShowRefundModal(false);
                    setSelectedBooking(null);
                    setRefundForm({ refund_amount: '', notes: '' });
                }}
                title="Process Refund"
            >
                <div className="space-y-4">
                    {selectedBooking && (
                        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                            <h4 className="font-medium text-gray-900">Booking: {selectedBooking.booking_reference}</h4>
                            <p className="text-sm text-gray-600">Apartment: {selectedBooking.apartment?.name}</p>
                            <p className="text-sm text-gray-600">User: {selectedBooking.user?.name}</p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Refund Amount ($) *</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={refundForm.refund_amount}
                            onChange={(e) => setRefundForm({ ...refundForm, refund_amount: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter refund amount"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                        <textarea
                            value={refundForm.notes}
                            onChange={(e) => setRefundForm({ ...refundForm, notes: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows={3}
                            placeholder="Enter notes for this refund"
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowRefundModal(false);
                                setSelectedBooking(null);
                                setRefundForm({ refund_amount: '', notes: '' });
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleProcessRefund}
                            disabled={isProcessingRefund || !refundForm.refund_amount}
                        >
                            {isProcessingRefund ? 'Processing...' : 'Process Refund'}
                        </Button>
                    </div>
                </div>
            </Modal>

        </div>
    );
};

export default BookingManagement;

